import React, { useEffect, useRef, useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";

import useDebouncedValue from "../../hooks/useDebouncedValue";
import "./CategorySearch.css";

// Matches the backend's rule: 2+ characters, or a lone digit ("5" → 5 mukhi)
const toSearchTerm = (value) => {
  const trimmed = value.trim();
  return trimmed.length >= 2 || /^\d$/.test(trimmed) ? trimmed : "";
};

/**
 * Search box for narrowing a category/subcategory listing. Calls
 * `onSearch` with the debounced term ("" when cleared / too short); the
 * page passes it to getProducts() alongside its category filter.
 * Remount it (via `key`) to reset when the category changes.
 */
export default function CategorySearch({ onSearch, placeholder = "Search in this category..." }) {
  const [value, setValue] = useState("");
  const term = useDebouncedValue(toSearchTerm(value), 350);

  // Skip the initial mount so the page's first fetch isn't duplicated
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    onSearch(term);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  return (
    <form
      className="category-search"
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        onSearch(toSearchTerm(value));
      }}
    >
      <FaSearch aria-hidden="true" />

      <input
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        autoComplete="off"
        enterKeyHint="search"
      />

      {value && (
        <button
          type="button"
          className="category-search-clear"
          onClick={() => setValue("")}
          aria-label="Clear search"
        >
          <FaTimes />
        </button>
      )}
    </form>
  );
}
